import { useState, useMemo } from "react"

import { useLocalization } from "@izettle/tenko-react"

import { CustomerEmailRegex, NameRegex } from "@/core/utils/constant"
import { Phone } from "@/modules/sign-up/api/types"
import { usePurchaseDetailFetch } from "@/modules/sign-up/api/use-purchase-detail"
import { Confirm, ConfirmProps } from "@/modules/sign-up/components/confirm"
import { ContactSent, ContactSentProps } from "@/modules/sign-up/components/contact-sent"
import { InputSlide, InputSlideProps } from "@/modules/sign-up/components/input-slide"
import { PhoneSlide, PhoneSlideProps } from "@/modules/sign-up/components/phone-slide"
import {
  RetrySendReceipt,
  RetrySendReceiptProps,
} from "@/modules/sign-up/components/retry-send-receipt"
import {
  SignUpSuccess,
  SignUpSuccessProps,
} from "@/modules/sign-up/components/sign-up-success"
import { ThankYou, ThankYouProps } from "@/modules/sign-up/components/thank-you"
import { SlideIndexs } from "@/modules/sign-up/components/types"
import { Welcome, WelcomeProps } from "@/modules/sign-up/components/welcome"

type FormProps = {
  firstName: string
  lastName: string
  email: string
  phone?: Phone
}

const initValues = {
  firstName: "",
  lastName: "",
  email: "",
  phone: undefined,
}

type SlidesType = Array<{
  key: string
  Slide: any
  props:
    | WelcomeProps
    | InputSlideProps
    | InputSlideProps
    | InputSlideProps
    | ConfirmProps
    | ContactSentProps
    | PhoneSlideProps
    | RetrySendReceiptProps
    | SignUpSuccessProps
    | ThankYouProps
}>

export const useLandingSlides = (purchaseUuid: string, orgUuid: string) => {
  const { text } = useLocalization()
  const [formValues, setFormValues] = useState<FormProps>(initValues)
  const [isEditName, setEditName] = useState(false)
  const [customerUuid, setCustomerUuid] = useState<string | null>(null)

  const { isFetching, data } = usePurchaseDetailFetch(orgUuid, purchaseUuid)

  const {
    orgName = "",
    orgLogoUrl,
    receiptAmount,
    receiptTime,
    orgPhoneNumber,
    orgContactEmail,
    orgPrivacyPolicyLink,
    failureMessage,
  } = data ?? {}

  const handleConfirmInput = (name: string, value: string | Phone, next: () => void) => {
    setFormValues({
      ...formValues,
      [name]: value,
    })
    next()
  }

  const slides: SlidesType = useMemo(() => {
    return [
      {
        key: "welcome",
        Slide: Welcome,
        props: {
          orgName,
          orgLogoUrl,
          purchaseUuid,
          orgPrivacyPolicyLink,
        },
      },
      {
        key: "first-name",
        Slide: InputSlide,
        props: {
          purchaseUuid,
          placeholder: text("SIGNUP_FIRST_NAME"),
          name: "firstName",
          type: "text",
          onConfirm: handleConfirmInput,
          fieldMatch: NameRegex,
          fieldMatchMessage: text("SIGNUP_VALID_NAME_REQUIRED"),
          nextSlide: SlideIndexs.LastName,
        },
      },
      {
        key: "last-name",
        Slide: InputSlide,
        props: {
          purchaseUuid,
          placeholder: text("SIGNUP_LAST_NAME"),
          name: "lastName",
          type: "text",
          onConfirm: handleConfirmInput,
          fieldMatch: NameRegex,
          fieldMatchMessage: text("SIGNUP_VALID_NAME_REQUIRED"),
          nextSlide: isEditName ? SlideIndexs.Confirm : SlideIndexs.Email,
          confirmText: isEditName ? text("SIGNUP_DONE") : undefined,
          animateSlide: isEditName,
        },
      },
      {
        key: "email",
        Slide: InputSlide,
        props: {
          purchaseUuid,
          placeholder: text("SIGNUP_ENTER_YOUR_EMAIL"),
          name: "email",
          type: "email",
          onConfirm: handleConfirmInput,
          fieldMatch: CustomerEmailRegex,
          fieldMatchMessage: text("SIGNUP_VALID_EMAIL_REQUIRED"),
          nextSlide: SlideIndexs.Confirm,
          confirmText: text("SIGNUP_DONE"),
        },
      },
      {
        key: "phone",
        Slide: PhoneSlide,
        props: {
          purchaseUuid,
          onConfirm: handleConfirmInput,
          confirmText: formValues.phone ? text("SIGNUP_SAVE") : text("SIGNUP_DONE"),
          nextSlide: SlideIndexs.Confirm,
        },
      },
      {
        key: "confirm",
        Slide: Confirm,
        props: {
          orgUuid,
          purchaseUuid,
          orgName,
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          email: formValues.email,
          phone: formValues.phone,
          orgPrivacyPolicyLink,
          onClickName: () => setEditName(true),
          setCustomerUuid,
        },
      },
      {
        key: "sign-up-success",
        Slide: SignUpSuccess,
        props: {
          orgName,
          amount: receiptAmount,
          time: receiptTime,
          email: formValues.email,
          phone: formValues.phone,
          purchaseUuid,
          customerUuid,
        },
      },
      {
        key: "retry-receipt-sent",
        Slide: RetrySendReceipt,
        props: {
          purchaseUuid,
          customerUuid,
        },
      },
      {
        key: "receipt-sent",
        Slide: ContactSent,
        props: {
          purchaseUuid,
        },
      },
      {
        key: "thank-you",
        Slide: ThankYou,
        props: {},
      },
    ]
  }, [
    text,
    orgName,
    orgLogoUrl,
    isEditName,
    formValues,
    orgUuid,
    purchaseUuid,
    receiptAmount,
    receiptTime,
    customerUuid,
  ])

  return {
    orgName,
    orgUuid,
    orgLogoUrl,
    orgPhoneNumber,
    orgContactEmail,
    orgPrivacyPolicyLink,
    isFetching,
    isEditName,
    customerUuid,
    purchaseUuid,
    slides,
    customerEmail: formValues.email,
    customerPhone: formValues.phone,
    failureMessage,
  }
}
